import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaCaretDown } from "react-icons/fa";
import useToaster from "../../hooks/useToaster";
import { Spinner } from "./Skeleton";
import { MdClose } from "react-icons/md";

/**
 * @component SearchableJournalSelect
 * @description A searchable dropdown that allows journal selection, grouped by "Assigned to me" and "Other Journals".
 *
 * @param {Object} props
 * @param {string} props.value - Current selected journal value (the 'value' property of the journal object).
 * @param {Function} props.onChange - Callback when a journal is selected.
 * @param {Object[]} props.assignedJournals - Journals assigned to the user, each with { value, label }.
 * @param {Object[]} props.otherJournals - All other journals, each with { value, label }.
 * @param {number} props.index - Index of this dropdown instance.
 * @param {boolean} props.isOpen - Whether this dropdown is currently open.
 * @param {Function} props.toggleDropdown - Function to toggle dropdown open/close.
 * @param {string[]} props.allPreferences - All selected journal values (used to detect duplicates).
 *
 * @returns {JSX.Element}
 */
const SearchableJournalSelect = ({
  value,
  onChange,
  assignedJournals = [],
  otherJournals = [],
  index,
  isOpen,
  toggleDropdown,
  allPreferences,
}) => {
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 280 });

  const allOptions = [
    { group: "Assigned to me", items: assignedJournals },
    { group: "Other Journals", items: otherJournals },
  ];

  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const maxDropdownHeight = 280; // max-h-[280px]
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const padding = 10; // padding from window edge

      // Check if there's enough space below
      const spaceBelow = windowHeight - rect.bottom - padding;
      const spaceAbove = rect.top - padding;

      // Position dropdown above if not enough space below
      let top = rect.bottom;
      let maxHeight = Math.min(maxDropdownHeight, spaceBelow);

      if (spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow) {
        // Show above
        maxHeight = Math.min(maxDropdownHeight, spaceAbove);
        top = rect.top - maxHeight;
      }

      // Adjust horizontal position if it goes off screen
      let left = rect.left;
      if (left + rect.width > windowWidth - padding) {
        left = windowWidth - rect.width - padding;
      }
      if (left < padding) {
        left = padding;
      }

      setDropdownPosition({
        top: top,
        left: left,
        width: rect.width,
        maxHeight: maxHeight,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        if (isOpen) toggleDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, toggleDropdown]);

  // Find the label for the current value
  const getLabelForValue = (val) => {
    const allJournals = [...assignedJournals, ...otherJournals];
    const journal = allJournals.find((j) => j.value === val);
    return journal ? journal.label : val;
  };

  // Filter options based on search
  const filteredOptions = allOptions.map(({ group, items }) => ({
    group,
    items: items.filter((j) =>
      j.label.toLowerCase().includes(search.toLowerCase())
    ),
  }));

  /**
   * @function handleSelect
   * @description Called when an option is selected.
   * @param {string} val - The 'value' property of the selected journal.
   */
  const handleSelect = (val) => {
    onChange(val, index); // Pass both the value and index
    setSearch("");
    toggleDropdown(null);
  };

  const dropdownContent = isOpen && (
    <ul
      ref={dropdownRef}
      className="bg-white border overflow-y-auto rounded-md shadow"
      style={{
        position: "fixed",
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        maxHeight: `${dropdownPosition.maxHeight}px`,
        zIndex: 9999,
      }}
    >
      {filteredOptions.map(({ group, items }) =>
        items.length > 0 ? (
          <React.Fragment key={group}>
            <li className="px-4 py-1 text-[13px] text-gray-400 font-semibold sticky top-0 bg-white">
              {group}
            </li>
            {items.map((item, idx) => (
              <li
                key={`${item.value}-${idx}`}
                onClick={() => handleSelect(item.value)}
                className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-[#35424D]"
              >
                <span className="px-4">{item.label}</span>
              </li>
            ))}
          </React.Fragment>
        ) : null
      )}
      {filteredOptions.every((opt) => opt.items.length === 0) && (
        <li className="px-4 py-2 text-gray-500">No options found</li>
      )}
    </ul>
  );

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? search : getLabelForValue(value)}
          onChange={(e) => setSearch(e.target.value)}
          onClick={() => toggleDropdown(index)}
          placeholder="Select Preferred Journal"
          className="w-full p-2 pr-10 border border-[#C3C3C3] text-[#868E94] text-[16px] font-medium rounded bg-white focus:border-[#35424D] focus:border focus:outline-none"
        />
        <FaCaretDown
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none"
          style={{ zIndex: 10 }}
        />
      </div>
      {isOpen && ReactDOM.createPortal(dropdownContent, document.body)}
      {/* Display warning if this value is a duplicate */}
      {value && allPreferences.filter((pref) => pref === value).length > 1 && (
        <div className="text-[#B95000] text-sm mt-1">
          ⚠ This option is already selected. Please choose a different one.
        </div>
      )}
    </div>
  );
};

const PreferenceModal = ({
  isOpen,
  closeModal,
  preferredJournals,
  setPreferredJournals,
  assignedJournals = [],
  otherJournals = [],
  onClose,
  loading = true,
}) => {
  const [preferences, setPreferences] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const showToast = useToaster();

  const MAX_PREFERENCES = 13;

  useEffect(() => {
    const init = preferredJournals.length
      ? [...preferredJournals]
      : ["", "", ""];
    setPreferences(init);
  }, [preferredJournals, isOpen]);

  /**
   * @function handleJournalChange
   * @description Update a specific preference value in the array.
   * @param {string} value - The 'value' property of the selected journal.
   * @param {number} index
   */
  const handleJournalChange = (value, index) => {
    setPreferences((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAdd = () => {
    if (preferences.length >= MAX_PREFERENCES) {
      showToast("You can add a maximum of 12 journal preferences.");
      return;
    }

    if (
      preferences.length < MAX_PREFERENCES &&
      preferences[preferences.length - 1]
    ) {
      setPreferences([...preferences, ""]);
    }
  };

  const handleDone = () => {
    const nonEmptyPreferences = preferences
      .map((p) => p.trim())
      .filter((p) => p !== "");
    const hasDuplicates =
      new Set(nonEmptyPreferences).size !== nonEmptyPreferences.length;

    if (hasDuplicates) {
      showToast(
        "Each Journal needs to be unique. Please select a different journal before submitting."
      );
    } else {
      setPreferredJournals(nonEmptyPreferences);
      closeModal(nonEmptyPreferences);
    }
  };

  const toggleDropdown = (index) => {
    setActiveDropdown((prev) => (prev === index ? null : index));
  };

  const EmptyPreferences = () => {
    setPreferences(["", "", ""]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black bg-opacity-40 flex items-center justify-center">
      {loading ? (
        <Spinner />
      ) : (
        <div
          className="bg-white rounded-md shadow-xl p-0 relative flex flex-col"
          style={{ width: "1088px", minHeight: "408px", maxHeight: "90vh" }}
        >
          {/* Header Section */}
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
            <h2 className="text-xl font-bold text-[#35424D] text-[22px]">
              Preference Settings
            </h2>
            <button onClick={onClose}>
              <MdClose className="text-[#35424D] w-[16px] h-[16px]" />
            </button>
          </div>

          {/* Content Section */}
          <div className="p-6 flex-grow overflow-y-auto">
            <div className="flex justify-between">
              <h3 className="text-[18px] font-semibold text-[#5D6871] mb-4">
                Journal Preference
              </h3>
              <button onClick={() => EmptyPreferences()}>
                <p className="text-[#1C40CA] underline font-bold">Clear All</p>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4 relative">
              {preferences.map((selected, index) => (
                <div key={index} className="relative">
                  <label className="block text-sm font-medium mb-1 text-[#35424D]">
                    {index + 1}
                  </label>
                  <SearchableJournalSelect
                    value={selected}
                    onChange={handleJournalChange}
                    assignedJournals={assignedJournals}
                    otherJournals={otherJournals}
                    index={index}
                    isOpen={activeDropdown === index}
                    toggleDropdown={toggleDropdown}
                    allPreferences={preferences}
                  />
                </div>
              ))}
            </div>

            <button
              onClick={handleAdd}
              disabled={
                !preferences[preferences.length - 1] ||
                preferences.length >= MAX_PREFERENCES
              }
              className={`flex items-center font-semibold border-2 text-[13px] px-2 py-1 rounded-md
    ${
      preferences.length >= MAX_PREFERENCES
        ? "text-gray-400 border-gray-300 cursor-not-allowed"
        : "text-[#1C40CA] border-[#1C40CA] hover:bg-gray-100"
    }`}
            >
              + Add More
            </button>
          </div>
          {/* Footer Section */}
          <div className="border-t border-gray-200 px-6 py-4 bg-white rounded-b-xl flex justify-end sticky bottom-0 z-20">
            <button
              onClick={handleDone}
              className="bg-[#1C40CA] text-white px-4 py-2 rounded hover:bg-[#1634A2] text-sm font-medium"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreferenceModal;
